import { useContext, useEffect, useRef } from 'react';
import { UserContext } from '../UserContext';
import { useTranslation } from '../LanguageContext';
import {
    applyTabAlert,
    resetTabAlert,
    showDesktopNotification,
} from '../utils/tabAlert';

/**
 * Mirrors the in-app unread badge into the browser tab.
 *
 * Renders nothing. It exists because on the web the Chats red dot is only
 * visible on a tab the user is not looking at — the moment that matters is
 * exactly the moment the dot cannot be seen.
 *
 * Native (Capacitor) builds already get FCM push notifications and have no tab
 * chrome to write to, so this stays a no-op there.
 */
export default function TabNotifier() {
    const { user } = useContext(UserContext);
    const { t } = useTranslation();

    const notifications = user?.notifications;
    const unreadCount = Array.isArray(notifications) ? notifications.length : 0;

    // Last count we raised a desktop notification for. Used to tell "a new
    // message arrived" from "the same unread state re-rendered" — an effect
    // that fires on every user-object change would otherwise pop a
    // notification on every keystroke elsewhere in the app.
    const lastNotifiedCount = useRef(unreadCount);
    const dialogues = user?.dialogues;

    useEffect(() => {
        const update = () => {
            applyTabAlert({
                count: unreadCount,
                hidden: document.visibilityState === 'hidden',
                flashMessage: unreadCount > 0 ? t('tab.newMessage') : null,
            });
        };

        update();
        document.addEventListener('visibilitychange', update);
        return () => document.removeEventListener('visibilitychange', update);
    }, [unreadCount, t]);

    // Desktop notification, only for counts that actually went up.
    useEffect(() => {
        if (unreadCount <= lastNotifiedCount.current) {
            lastNotifiedCount.current = unreadCount;
            return;
        }
        lastNotifiedCount.current = unreadCount;

        const senderId = notifications[notifications.length - 1];
        const sender = Array.isArray(dialogues)
            ? dialogues.find((d) => d.id === senderId)
            : null;

        showDesktopNotification({
            title: sender?.username || t('tab.newMessage'),
            body: t('tab.newMessageBody'),
            // One notification per conversation, replaced rather than stacked.
            tag: `chat-${senderId}`,
            onClick: () => {
                window.location.href = '/chats';
            },
        });
    }, [unreadCount, notifications, dialogues, t]);

    // Leave the tab as we found it — otherwise logging out strands a "(3)" in
    // the title of a page with no unread messages.
    useEffect(() => () => {
        applyTabAlert({ count: 0, hidden: false, flashMessage: null });
        resetTabAlert();
    }, []);

    return null;
}
